import { Link } from 'react-router-dom'

import { MiniTrajectory } from './Charts.jsx'
import { TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { teamStyle } from './colors.js'
import { useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { teamsModel } from './leagueModel.js'
import { teamPath } from './paths.js'
import { READY } from './poll.js'
import { backState } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// Teams: the thirty, as tiles. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// A TILE IS A LINK, WASHED IN THE TEAM'S COLOUR, WITH THE SEASON AS A LINE
// ═══════════════════════════════════════════════════════════════════════════
//
// City over nickname, the rank in the conference, the record and the season as games over .500 on the same shared scale
// the standings use (so a tile's line compares with another's). Two tiles across at 320px, more as the room allows. The
// grid is built from the standings file alone: it has a row for every team and the team files are thirty more requests
// for nothing a tile draws (`RECONCILIATION-seasons.md`: every team has a file, so the design's fallback is unused,
// and the thirty are read one at a time on the page of the team that is opened).
//
// A tile is `<a>`, so it is a path somebody can paste (`/hoops/teams/1610612760?season=2025-26`) and Back returns here.
// A finished season's wash is the full one, and a stale one's (a season marked current whose file has stopped
// refreshing) fades through `data-stale` on the view's root.

function TeamsBody() {
  useDocumentTitle('Teams')
  const choice = useSeasonChoice()
  const teams = useTeams()
  const season = choice.selected?.season ?? null
  const file = useHistoryResource('standings', season)
  const ready = file.state === READY
  const head = useHead({ season, entry: choice.selected, asOf: file.data?.as_of, source: file.data?.source, ready })

  if (choice.selected === null) return null

  const model = ready
    ? teamsModel({ standings: file.data, namesOf: (teamId, abbr, full) => teams.namesOf(teamId, abbr, full) })
    : null

  return (
    <div className="d2d-hoops__view" data-testid="hoops-teams" data-stale={head.stale ? 'true' : 'false'}>
      <SeasonHead choice={choice} unavailable={NONE} head={head} title={model ? `${model.count} teams.` : null} back={null} />
      {model === null ? null : (
        <>
          <p className="d2d-hoops__panelnote2">
            Each line is the season in games over .500, on one scale for every team. The dotted line is .500.
          </p>
          {model.groups.map((group) => (
            <section className="d2d-hoops__tilegroup" aria-label={group.name} key={group.name} data-testid={`hoops-teams-${group.name}`}>
              <span className="d2d-hoops__paneltitle d2d-micro">{group.name}</span>
              <div className="d2d-hoops__tilegrid2">
                {group.rows.map((row) => (
                  <Link
                    key={row.teamId}
                    to={teamPath(row.teamId, season)}
                    state={backState('Teams')}
                    className="d2d-hoops__teamtile"
                    style={teamStyle(teams.byId(row.teamId))}
                    data-testid="hoops-team-tile"
                    data-team-id={row.teamId}
                  >
                    <span className="d2d-hoops__tilehead">
                      <TeamMark team={teams.byId(row.teamId)} tricode={row.abbr} which="away" />
                      <span className="d2d-hoops__tilerank d2d-micro">{row.rank}</span>
                    </span>
                    <span className="d2d-hoops__tilename">
                      <span className="d2d-hoops__tilecity d2d-micro">{row.city}</span>
                      <span className="d2d-hoops__tilenick">{row.nick}</span>
                    </span>
                    <span className="d2d-hoops__tilefoot">
                      <span className="d2d-hoops__tilerecord">{row.record}</span>
                      <span className="d2d-hoops__sparkbox d2d-hoops__sparkbox--tile">
                        <MiniTrajectory mini={row.mini} />
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  )
}

const NONE = new Set()

export default function TeamsView() {
  return (
    <ViewFrame tab="teams">
      <TeamsBody />
    </ViewFrame>
  )
}
